import type React from 'react';
import { useState, useCallback, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogActions,
  DialogContent,
  Button,
  Input,
  Switch,
  Text,
  Tooltip,
  TabList,
  Tab,
  Dropdown,
  Combobox,
  Option,
  Spinner,
  mergeClasses,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
} from '@fluentui/react-components';
import {
  SettingsRegular,
  ClockRegular,
  Speaker2Regular,
  ArrowUploadRegular,
  PlayRegular,
  DeleteRegular,
  DesktopRegular,
  AlertRegular,
  PeopleTeamRegular,
  AddRegular,
  CheckmarkRegular,
  ErrorCircleRegular,
  InfoRegular,
  MoreHorizontalRegular,
  TagRegular,
} from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import {
  IncidentSeverity,
  type DiscoverableGroup,
  type StatusPanelSettings,
  type SlaTiming,
  type SeveritySoundConfig,
  createDefaultSettings,
} from 'types';
import type { GroupConfig, SocRole, NotificationCapabilities } from 'types/notifications';
import { getDiscoverableGroups, getNotificationCapabilities } from '../../services/api';
import { useDebouncedSettingsSave, useIsMobile } from 'hooks';
import { useSettingsDialogStyles } from './SettingsDialog.styles';
import { SeverityBadge } from './SeverityBadge';
import { NotificationRuleMatrix } from './NotificationRuleMatrix';

interface SettingsDialogProps {
  settings: StatusPanelSettings;
  onSave: (settings: StatusPanelSettings) => Promise<void>;
  trigger?: React.ReactElement;
}

const SEVERITY_ORDER = [
  IncidentSeverity.High,
  IncidentSeverity.Medium,
  IncidentSeverity.Low,
  IncidentSeverity.Informational,
];

type SettingsTab = 'sla' | 'sound' | 'notifications' | 'securityGroups' | 'wallScreen' | 'nis2Categories';

const TAB_ITEMS: Array<{ value: SettingsTab; labelKey: string; icon: React.ReactElement }> = [
  { value: 'sla', labelKey: 'statusPanel.settings.slaTimers', icon: <ClockRegular /> },
  { value: 'sound', labelKey: 'statusPanel.settings.soundNotifications', icon: <Speaker2Regular /> },
  { value: 'notifications', labelKey: 'statusPanel.settings.notifications', icon: <AlertRegular /> },
  { value: 'securityGroups', labelKey: 'statusPanel.settings.securityGroups', icon: <PeopleTeamRegular /> },
  { value: 'wallScreen', labelKey: 'statusPanel.settings.wallScreenSettings', icon: <DesktopRegular /> },
  { value: 'nis2Categories' as SettingsTab, labelKey: 'statusPanel.tags.settingsTitle', icon: <TagRegular /> },
];

const MOBILE_PRIMARY_TABS: SettingsTab[] = ['sla', 'sound'];

export const SettingsDialog: React.FC<SettingsDialogProps> = ({
  settings,
  onSave,
  trigger,
}) => {
  const styles = useSettingsDialogStyles();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState<SettingsTab>('sla');
  const [localSettings, setLocalSettings] = useState<StatusPanelSettings>(settings);
  const [notificationCapabilities, setNotificationCapabilities] = useState<NotificationCapabilities | null>(null);
  const [discoverableGroups, setDiscoverableGroups] = useState<DiscoverableGroup[]>([]);
  const [discoverableGroupsLoading, setDiscoverableGroupsLoading] = useState(false);
  const [discoverableGroupsError, setDiscoverableGroupsError] = useState<string | null>(null);
  const [lastSavedTime, setLastSavedTime] = useState<Date | null>(null);
  const [nis2NewCategory, setNis2NewCategory] = useState('');
  const fileInputRefs = useRef<Record<number, HTMLInputElement | null>>({});

  // Use debounced save hook for auto-save
  const { debouncedSave, isSaving, saveError, cancelSave } = useDebouncedSettingsSave(
    async (newSettings) => {
      await onSave(newSettings);
      setLastSavedTime(new Date());
    }
  );

  // Fetch notification capabilities on mount
  useEffect(() => {
    getNotificationCapabilities()
      .then(setNotificationCapabilities)
      .catch((err) => console.warn('Failed to fetch notification capabilities:', err));
  }, []);

  // Fetch discoverable groups for current user via delegated Graph when settings dialog opens
  useEffect(() => {
    if (!open) {
      return;
    }

    setDiscoverableGroupsLoading(true);
    setDiscoverableGroupsError(null);

    getDiscoverableGroups()
      .then((groups) => setDiscoverableGroups(groups))
      .catch((err) => {
        console.warn('Failed to fetch discoverable groups:', err);
        setDiscoverableGroupsError(t('statusPanel.settings.groupDiscoveryLoadFailed'));
      })
      .finally(() => setDiscoverableGroupsLoading(false));
  }, [open, t]);

  const handleOpen = useCallback(() => {
    setLocalSettings(settings);
    setSelectedTab('sla');
    setLastSavedTime(null);
    setOpen(true);
  }, [settings]);

  const handleCancel = useCallback(() => {
    cancelSave();
    setOpen(false);
  }, [cancelSave]);

  const applySettingsUpdate = useCallback(
    (updater: (prev: StatusPanelSettings) => StatusPanelSettings) => {
      setLocalSettings((prev) => {
        const next = updater(prev);
        if (open) {
          debouncedSave(next);
        }
        return next;
      });
    },
    [open, debouncedSave]
  );

  const handleReset = useCallback(() => {
    applySettingsUpdate(() => createDefaultSettings());
  }, [applySettingsUpdate]);

  const updateSlaTiming = useCallback(
    (severity: IncidentSeverity, field: keyof SlaTiming, value: number) => {
      applySettingsUpdate((prev) => {
        const updated = {
          ...prev,
          slaTimings: prev.slaTimings.map((timing) =>
            timing.severity === severity ? { ...timing, [field]: value } : timing
          ),
        };
        return updated;
      });
    },
    [applySettingsUpdate]
  );

  const updateSoundConfig = useCallback(
    (severity: IncidentSeverity, updates: Partial<SeveritySoundConfig>) => {
      applySettingsUpdate((prev) => {
        const updated = {
          ...prev,
          soundConfigs: prev.soundConfigs.map((config) =>
            config.severity === severity ? { ...config, ...updates } : config
          ),
        };
        return updated;
      });
    },
    [applySettingsUpdate]
  );

  const handleFileUpload = useCallback(
    (severity: IncidentSeverity, file: File) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64Data = reader.result as string;
        updateSoundConfig(severity, {
          soundFileData: base64Data,
          soundFileName: file.name,
        });
      };
      reader.readAsDataURL(file);
    },
    [updateSoundConfig]
  );

  const handleFileInputChange = useCallback(
    (severity: IncidentSeverity) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        handleFileUpload(severity, file);
      }
      // Reset input so same file can be selected again
      event.target.value = '';
    },
    [handleFileUpload]
  );

  const handleRemoveSound = useCallback(
    (severity: IncidentSeverity) => {
      updateSoundConfig(severity, {
        soundFileData: null,
        soundFileName: null,
      });
    },
    [updateSoundConfig]
  );

  const handleTestSound = useCallback((severity: IncidentSeverity) => {
    const config = localSettings.soundConfigs.find((c) => c.severity === severity);
    if (config?.soundFileData) {
      const audio = new Audio(config.soundFileData);
      audio.play().catch((err) => {
        console.error('Failed to play sound:', err);
      });
    }
  }, [localSettings.soundConfigs]);

  const getSlaTiming = (severity: IncidentSeverity): SlaTiming => {
    return (
      localSettings.slaTimings.find((t) => t.severity === severity) ?? {
        severity,
        ackSlaMinutes: 15,
        updateSlaMinutes: 30,
      }
    );
  };

  const getSoundConfig = (severity: IncidentSeverity): SeveritySoundConfig => {
    return (
      localSettings.soundConfigs.find((c) => c.severity === severity) ?? {
        severity,
        enabled: false,
        soundFileData: null,
        soundFileName: null,
        soundFileUrl: null,
      }
    );
  };

  const handleAddGroup = useCallback(() => {
    applySettingsUpdate((prev) => ({
      ...prev,
      groupConfigs: [
        ...prev.groupConfigs,
        {
          groupId: '',
          displayName: '',
          assignedRoles: [],
          cachedMemberCount: 0,
          cachedMemberCountTime: null,
        },
      ],
    }));
  }, [applySettingsUpdate]);

  const handleRemoveGroup = useCallback((index: number) => {
    applySettingsUpdate((prev) => ({
      ...prev,
      groupConfigs: prev.groupConfigs.filter((_, i) => i !== index),
    }));
  }, [applySettingsUpdate]);

  const handleUpdateGroup = useCallback(
    (index: number, updates: Partial<GroupConfig>) => {
      applySettingsUpdate((prev) => ({
        ...prev,
        groupConfigs: prev.groupConfigs.map((group, i) =>
          i === index ? { ...group, ...updates } : group
        ),
      }));
    },
    [applySettingsUpdate]
  );

  const handleRolesChange = useCallback(
    (index: number, selectedRoles: string[]) => {
      handleUpdateGroup(index, { assignedRoles: selectedRoles as SocRole[] });
    },
    [handleUpdateGroup]
  );

  const addNis2Category = useCallback((value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    applySettingsUpdate((prev) => ({
      ...prev,
      nis2Categories: [...(prev.nis2Categories ?? []), trimmed],
    }));
    setNis2NewCategory('');
  }, [applySettingsUpdate]);

  const removeNis2Category = useCallback((idx: number) => {
    applySettingsUpdate((prev) => ({
      ...prev,
      nis2Categories: (prev.nis2Categories ?? []).filter((_, i) => i !== idx),
    }));
  }, [applySettingsUpdate]);

  const getFilteredDiscoverableGroups = useCallback((query: string) => {
    const term = query.trim().toLowerCase();
    const filtered = term.length === 0
      ? discoverableGroups
      : discoverableGroups.filter((group) =>
        group.displayName.toLowerCase().includes(term) ||
        group.groupId.toLowerCase().includes(term)
      );

    return filtered.slice(0, 5);
  }, [discoverableGroups]);

  const renderInfoIcon = useCallback((content: string) => (
    <Tooltip content={content} relationship="description">
      <span className={styles.infoIconWrap}>
        <InfoRegular className={styles.infoIcon} />
      </span>
    </Tooltip>
  ), [styles.infoIcon, styles.infoIconWrap]);

  const getTabLabel = useCallback((tab: SettingsTab) => {
    const item = TAB_ITEMS.find((entry) => entry.value === tab);
    return item ? t(item.labelKey) : '';
  }, [t]);

  const isSelectedTabInPrimary = MOBILE_PRIMARY_TABS.includes(selectedTab);
  const mobileOverflowLabel = isSelectedTabInPrimary
    ? t('statusPanel.moreActions')
    : getTabLabel(selectedTab);

  return (
    <Dialog open={open} onOpenChange={(_, data) => data.open ? handleOpen() : setOpen(false)}>
      <DialogTrigger disableButtonEnhancement>
        {trigger ?? (
          <Button icon={<SettingsRegular />} appearance="subtle" size="small">
            {t('statusPanel.settings.title')}
          </Button>
        )}
      </DialogTrigger>
      <DialogSurface className={mergeClasses(styles.dialogSurface, isMobile && styles.dialogSurfaceMobile)}>
        <DialogTitle>{t('statusPanel.settings.title')}</DialogTitle>
        <DialogContent className={styles.dialogContent}>
          <div className={mergeClasses(styles.dialogBody, isMobile && styles.dialogBodyMobile)}>
            {!isMobile && (
              <TabList
                className={styles.tabListVertical}
                vertical
                selectedValue={selectedTab}
                onTabSelect={(_, data) => setSelectedTab(data.value as SettingsTab)}
              >
                {TAB_ITEMS.map((tab) => (
                  <Tab key={tab.value} icon={tab.icon} value={tab.value}>
                    {t(tab.labelKey)}
                  </Tab>
                ))}
              </TabList>
            )}

            {isMobile && (
              <div className={styles.mobileTabsBar}>
                {MOBILE_PRIMARY_TABS.map((tab) => (
                  <Button
                    key={tab}
                    appearance={selectedTab === tab ? 'primary' : 'secondary'}
                    className={styles.mobileTabButton}
                    onClick={() => setSelectedTab(tab)}
                  >
                    {getTabLabel(tab)}
                  </Button>
                ))}
                <Menu>
                  <MenuTrigger disableButtonEnhancement>
                    <Button
                      appearance={isSelectedTabInPrimary ? 'secondary' : 'primary'}
                      icon={<MoreHorizontalRegular />}
                      className={styles.mobileTabButton}
                    >
                      {mobileOverflowLabel}
                    </Button>
                  </MenuTrigger>
                  <MenuPopover>
                    <MenuList>
                      {TAB_ITEMS.filter((tab) => !MOBILE_PRIMARY_TABS.includes(tab.value)).map((tab) => (
                        <MenuItem key={tab.value} icon={tab.icon} onClick={() => setSelectedTab(tab.value)}>
                          {t(tab.labelKey)}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </MenuPopover>
                </Menu>
              </div>
            )}

            {/* SLA Timers Tab */}
            {selectedTab === 'sla' && (
              <div className={styles.tabContent}>
                {!isMobile && (
                  <div className={styles.severityRowHeader}>
                    <span>{t('statusPanel.settings.severity')}</span>
                    <span className={styles.headerWithInfo}>
                      {t('statusPanel.settings.ackSla')}
                      {renderInfoIcon(t('statusPanel.settings.tooltips.ackSla'))}
                    </span>
                    <span className={styles.headerWithInfo}>
                      {t('statusPanel.settings.updateSla')}
                      {renderInfoIcon(t('statusPanel.settings.tooltips.updateSla'))}
                    </span>
                  </div>
                )}
                {SEVERITY_ORDER.map((severity) => {
                  const timing = getSlaTiming(severity);
                  if (isMobile) {
                    return (
                      <div key={severity} className={styles.mobileCard}>
                        <div className={styles.mobileCardHeader}>
                          <SeverityBadge severity={severity} size="medium" />
                        </div>
                        <div className={styles.mobileCardRow}>
                          <Text className={styles.mobileLabel}>{t('statusPanel.settings.ackSla')}</Text>
                          <Input
                            type="number"
                            min={1}
                            value={String(timing.ackSlaMinutes)}
                            onChange={(_, data) =>
                              updateSlaTiming(severity, 'ackSlaMinutes', Number(data.value) || 1)
                            }
                            contentAfter={<Text size={200}>{t('statusPanel.settings.minutes')}</Text>}
                            className={mergeClasses(styles.inputField, styles.mobileField)}
                          />
                        </div>
                        <div className={styles.mobileCardRow}>
                          <Text className={styles.mobileLabel}>{t('statusPanel.settings.updateSla')}</Text>
                          <Input
                            type="number"
                            min={1}
                            value={String(timing.updateSlaMinutes)}
                            onChange={(_, data) =>
                              updateSlaTiming(severity, 'updateSlaMinutes', Number(data.value) || 1)
                            }
                            contentAfter={<Text size={200}>{t('statusPanel.settings.minutes')}</Text>}
                            className={mergeClasses(styles.inputField, styles.mobileField)}
                          />
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div key={severity} className={styles.severityRow}>
                      <SeverityBadge severity={severity} size="medium" />
                      <Input
                        type="number"
                        min={1}
                        value={String(timing.ackSlaMinutes)}
                        onChange={(_, data) =>
                          updateSlaTiming(severity, 'ackSlaMinutes', Number(data.value) || 1)
                        }
                        contentAfter={<Text size={200}>{t('statusPanel.settings.minutes')}</Text>}
                        className={styles.inputField}
                      />
                      <Input
                        type="number"
                        min={1}
                        value={String(timing.updateSlaMinutes)}
                        onChange={(_, data) =>
                          updateSlaTiming(severity, 'updateSlaMinutes', Number(data.value) || 1)
                        }
                        contentAfter={<Text size={200}>{t('statusPanel.settings.minutes')}</Text>}
                        className={styles.inputField}
                      />
                    </div>
                  );
                })}
              </div>
            )}

            {/* Sound Notifications Tab */}
            {selectedTab === 'sound' && (
              <div className={styles.tabContent}>
                <Text size={200} style={{ color: 'var(--colorNeutralForeground3)', marginBottom: '12px', display: 'block' }}>
                  {t('statusPanel.settings.soundDescription')}
                </Text>
                {!isMobile && (
                  <div className={styles.soundRowHeader}>
                    <span>{t('statusPanel.settings.severity')}</span>
                    <span className={styles.headerWithInfo}>
                      {t('statusPanel.settings.enabled')}
                      {renderInfoIcon(t('statusPanel.settings.tooltips.soundEnabled'))}
                    </span>
                    <span className={styles.headerWithInfo}>
                      {t('statusPanel.settings.soundFile')}
                      {renderInfoIcon(t('statusPanel.settings.tooltips.soundFile'))}
                    </span>
                    <span>{t('statusPanel.settings.actions')}</span>
                  </div>
                )}
                {SEVERITY_ORDER.map((severity) => {
                  const config = getSoundConfig(severity);
                  if (isMobile) {
                    return (
                      <div key={severity} className={styles.mobileCard}>
                        <div className={styles.mobileCardHeader}>
                          <SeverityBadge severity={severity} size="medium" />
                          <Switch
                            checked={config.enabled}
                            onChange={(_, data) =>
                              updateSoundConfig(severity, { enabled: data.checked })
                            }
                          />
                        </div>
                        <div className={styles.mobileCardRow}>
                          <Text className={styles.mobileLabel}>{t('statusPanel.settings.soundFile')}</Text>
                          <span className={config.soundFileName ? styles.fileName : styles.noFile}>
                            {config.soundFileName ?? t('statusPanel.settings.noFileSelected')}
                          </span>
                        </div>
                        <div className={styles.mobileActionsRow}>
                          <input
                            type="file"
                            accept="audio/*"
                            className={styles.fileInput}
                            ref={(el) => { fileInputRefs.current[severity] = el; }}
                            onChange={handleFileInputChange(severity)}
                          />
                          <Button
                            icon={<ArrowUploadRegular />}
                            appearance="subtle"
                            className={styles.mobileIconButton}
                            onClick={() => fileInputRefs.current[severity]?.click()}
                          >
                            {t('statusPanel.settings.uploadSound')}
                          </Button>
                          {config.soundFileData && (
                            <>
                              <Button
                                icon={<PlayRegular />}
                                appearance="subtle"
                                className={styles.mobileIconButton}
                                onClick={() => handleTestSound(severity)}
                              >
                                {t('statusPanel.settings.testSound')}
                              </Button>
                              <Button
                                icon={<DeleteRegular />}
                                appearance="subtle"
                                className={styles.mobileIconButton}
                                onClick={() => handleRemoveSound(severity)}
                              >
                                {t('statusPanel.settings.removeSound')}
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div key={severity} className={styles.soundRow}>
                      <SeverityBadge severity={severity} size="medium" />
                      <Switch
                        checked={config.enabled}
                        onChange={(_, data) =>
                          updateSoundConfig(severity, { enabled: data.checked })
                        }
                      />
                      <span className={config.soundFileName ? styles.fileName : styles.noFile}>
                        {config.soundFileName ?? t('statusPanel.settings.noFileSelected')}
                      </span>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <input
                          type="file"
                          accept="audio/*"
                          className={styles.fileInput}
                          ref={(el) => { fileInputRefs.current[severity] = el; }}
                          onChange={handleFileInputChange(severity)}
                        />
                        <Tooltip content={t('statusPanel.settings.uploadSound')} relationship="label">
                          <Button
                            icon={<ArrowUploadRegular />}
                            size="small"
                            appearance="subtle"
                            onClick={() => fileInputRefs.current[severity]?.click()}
                            className={styles.uploadButton}
                          />
                        </Tooltip>
                        {config.soundFileData && (
                          <>
                            <Tooltip content={t('statusPanel.settings.testSound')} relationship="label">
                              <Button
                                icon={<PlayRegular />}
                                size="small"
                                appearance="subtle"
                                onClick={() => handleTestSound(severity)}
                                className={styles.testButton}
                              />
                            </Tooltip>
                            <Tooltip content={t('statusPanel.settings.removeSound')} relationship="label">
                              <Button
                                icon={<DeleteRegular />}
                                size="small"
                                appearance="subtle"
                                onClick={() => handleRemoveSound(severity)}
                              />
                            </Tooltip>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Notifications Tab */}
            {selectedTab === 'notifications' && (
              <div className={styles.tabContent}>
                <Text size={200} style={{ color: 'var(--colorNeutralForeground3)', marginBottom: '12px', display: 'block' }}>
                  {t('statusPanel.settings.notificationsDescription')}
                </Text>
                <NotificationRuleMatrix
                  rules={localSettings.notificationRules}
                  groups={localSettings.groupConfigs}
                  capabilities={notificationCapabilities ?? undefined}
                  onRuleChange={(updatedRule) => {
                    applySettingsUpdate((prev) => {
                      const existingIndex = prev.notificationRules.findIndex(
                        (r) => r.severity === updatedRule.severity && r.customerId === updatedRule.customerId
                      );
                      if (existingIndex >= 0) {
                        // Update existing rule
                        return {
                          ...prev,
                          notificationRules: prev.notificationRules.map((rule, idx) =>
                            idx === existingIndex ? updatedRule : rule
                          ),
                        };
                      } else {
                        // Add new rule
                        return {
                          ...prev,
                          notificationRules: [...prev.notificationRules, updatedRule],
                        };
                      }
                    });
                  }}
                />
              </div>
            )}

            {/* Security Groups Tab */}
            {selectedTab === 'securityGroups' && (
              <div className={styles.tabContent}>
                <Text size={200} style={{ color: 'var(--colorNeutralForeground3)', marginBottom: '12px', display: 'block' }}>
                  {t('statusPanel.settings.securityGroupsDescription')}
                </Text>
                {discoverableGroupsLoading && (
                  <Text size={200} style={{ color: 'var(--colorNeutralForeground3)' }}>
                    {t('statusPanel.settings.groupDiscoveryLoading')}
                  </Text>
                )}
                {discoverableGroupsError && (
                  <Text size={200} style={{ color: 'var(--colorPaletteRedForeground1)' }}>
                    {discoverableGroupsError}
                  </Text>
                )}

                {localSettings.groupConfigs.length === 0 ? (
                  <div className={styles.emptyState}>
                    <PeopleTeamRegular style={{ fontSize: '48px', marginBottom: '12px' }} />
                    <Text>{t('statusPanel.settings.noGroupsConfigured')}</Text>
                    <Button
                      icon={<AddRegular />}
                      appearance="primary"
                      style={{ marginTop: '16px' }}
                      onClick={handleAddGroup}
                    >
                      {t('statusPanel.settings.addGroup')}
                    </Button>
                  </div>
                ) : (
                  <>
                    {!isMobile && (
                      <div className={styles.groupRowHeader}>
                        <span className={styles.headerWithInfo}>
                          {t('statusPanel.settings.groupId')}
                          {renderInfoIcon(t('statusPanel.settings.tooltips.groupId'))}
                        </span>
                        <span className={styles.headerWithInfo}>
                          {t('statusPanel.settings.displayName')}
                          {renderInfoIcon(t('statusPanel.settings.tooltips.displayName'))}
                        </span>
                        <span className={styles.headerWithInfo}>
                          {t('statusPanel.settings.roles')}
                          {renderInfoIcon(t('statusPanel.settings.tooltips.assignedRoles'))}
                        </span>
                        <span>{t('statusPanel.settings.actions')}</span>
                      </div>
                    )}
                    {localSettings.groupConfigs.map((group, index) => (
                      <div key={index} className={isMobile ? styles.mobileCard : styles.groupRow}>
                        {isMobile && (
                          <div className={styles.mobileCardHeader}>
                            <Text className={styles.mobileLabel}>{t('statusPanel.settings.securityGroups')}</Text>
                            <Button
                              icon={<DeleteRegular />}
                              appearance="subtle"
                              className={styles.mobileIconButton}
                              onClick={() => handleRemoveGroup(index)}
                            />
                          </div>
                        )}
                        {isMobile && <Text className={styles.mobileLabel}>{t('statusPanel.settings.groupId')}</Text>}
                        <Combobox
                          placeholder={t('statusPanel.settings.placeholders.groupId')}
                          value={group.groupId}
                          onChange={(event) => {
                            const value = (event.target as HTMLInputElement).value;
                            handleUpdateGroup(index, { groupId: value });
                          }}
                          onOptionSelect={(_, data) => {
                            const selectedGroupId = data.optionValue;
                            if (!selectedGroupId) {
                              return;
                            }

                            const selected = discoverableGroups.find((g) => g.groupId === selectedGroupId);
                            handleUpdateGroup(index, {
                              groupId: selectedGroupId,
                              displayName: group.displayName || selected?.displayName || group.displayName,
                            });
                          }}
                          className={styles.inputField}
                        >
                          {getFilteredDiscoverableGroups(group.groupId).map((discoverable) => (
                            <Option
                              key={discoverable.groupId}
                              value={discoverable.groupId}
                              text={`${discoverable.displayName} (${discoverable.groupId})`}
                            >
                              {discoverable.displayName} ({discoverable.groupId})
                            </Option>
                          ))}
                        </Combobox>
                        {isMobile && <Text className={styles.mobileLabel}>{t('statusPanel.settings.displayName')}</Text>}
                        <Input
                          placeholder={t('statusPanel.settings.placeholders.displayName')}
                          value={group.displayName}
                          onChange={(_, data) =>
                            handleUpdateGroup(index, { displayName: data.value })
                          }
                          className={styles.inputField}
                        />
                        {isMobile && <Text className={styles.mobileLabel}>{t('statusPanel.settings.roles')}</Text>}
                        <Dropdown
                          key={`roles-${index}-${group.assignedRoles.join(',')}`}
                          multiselect
                          placeholder={t('statusPanel.settings.roles')}
                          selectedOptions={group.assignedRoles}
                          value={group.assignedRoles.length > 0
                            ? group.assignedRoles.map(r => r === 'Operator' ? t('statusPanel.settings.operatorRole') : t('statusPanel.settings.adminRole')).join(', ')
                            : undefined
                          }
                          onOptionSelect={(_, data) =>
                            handleRolesChange(index, data.selectedOptions)
                          }
                          style={{ minWidth: '140px' }}
                        >
                          <Option value="Operator" text={t('statusPanel.settings.operatorRole')}>
                            {t('statusPanel.settings.operatorRole')}
                          </Option>
                          <Option value="Admin" text={t('statusPanel.settings.adminRole')}>
                            {t('statusPanel.settings.adminRole')}
                          </Option>
                        </Dropdown>
                        {!isMobile && (
                          <Tooltip content={t('statusPanel.settings.removeGroup')} relationship="label">
                            <Button
                              icon={<DeleteRegular />}
                              size="small"
                              appearance="subtle"
                              onClick={() => handleRemoveGroup(index)}
                            />
                          </Tooltip>
                        )}
                      </div>
                    ))}
                    <Button
                      icon={<AddRegular />}
                      appearance="subtle"
                      size={isMobile ? 'large' : 'medium'}
                      onClick={handleAddGroup}
                      style={{ alignSelf: 'flex-start', marginTop: '8px' }}
                    >
                      {t('statusPanel.settings.addGroup')}
                    </Button>
                  </>
                )}
              </div>
            )}

            {/* Wall Screen Settings Tab */}
            {selectedTab === 'wallScreen' && (
              <div className={styles.tabContent}>
                <Text size={200} style={{ color: 'var(--colorNeutralForeground3)', marginBottom: '12px', display: 'block' }}>
                  {t('statusPanel.settings.wallScreenDescription')}
                </Text>
                <div style={{ display: 'flex', alignItems: isMobile ? 'stretch' : 'center', gap: '12px', flexDirection: isMobile ? 'column' : 'row' }}>
                  <div className={styles.headerWithInfo}>
                    <Text>{t('statusPanel.settings.staleThreshold')}</Text>
                    {renderInfoIcon(t('statusPanel.settings.tooltips.staleThreshold'))}
                  </div>
                  <Input
                    type="number"
                    min={1}
                    max={168}
                    value={String(localSettings.staleThresholdHours)}
                    onChange={(_, data) =>
                      applySettingsUpdate((prev) => ({
                        ...prev,
                        staleThresholdHours: Math.max(1, Math.min(168, Number(data.value) || 8)),
                      }))
                    }
                    contentAfter={<Text size={200}>{t('statusPanel.settings.hours')}</Text>}
                    style={{ width: isMobile ? '100%' : '120px' }}
                    className={isMobile ? styles.mobileField : undefined}
                  />
                </div>
              </div>
            )}
            {/* NIS2 Categories Tab */}
            {selectedTab === 'nis2Categories' && (
              <div className={styles.tabContent}>
                <Text size={200} style={{ color: 'var(--colorNeutralForeground3)', marginBottom: '12px', display: 'block' }}>
                  {t('statusPanel.tags.settingsDescription')}
                </Text>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
                  {(localSettings.nis2Categories ?? []).length === 0 ? (
                    <Text size={300} style={{ color: 'var(--colorNeutralForeground3)' }}>
                      {t('statusPanel.tags.noCategoriesConfigured')}
                    </Text>
                  ) : (
                    (localSettings.nis2Categories ?? []).map((cat, idx) => (
                      <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Text style={{ flex: 1 }}>{cat}</Text>
                        <Button
                          size="small"
                          appearance="subtle"
                          icon={<DeleteRegular />}
                          onClick={() => removeNis2Category(idx)}
                        >
                          {t('statusPanel.tags.removeCategory')}
                        </Button>
                      </div>
                    ))
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Input
                    placeholder={t('statusPanel.tags.categoryPlaceholder')}
                    value={nis2NewCategory}
                    onChange={(_, data) => setNis2NewCategory(data.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        addNis2Category(nis2NewCategory);
                      }
                    }}
                    style={{ flex: 1 }}
                  />
                  <Button
                    appearance="primary"
                    size="small"
                    icon={<AddRegular />}
                    disabled={!nis2NewCategory.trim()}
                    onClick={() => addNis2Category(nis2NewCategory)}
                  >
                    {t('statusPanel.tags.addCategory')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <div className={isMobile ? styles.dialogActionsMobile : styles.saveStatusWrap}>
            <div className={styles.saveStatusWrap}>
            {isSaving && (
              <>
                <Spinner size="tiny" />
                <Text size={200} style={{ color: 'var(--colorBrandForeground1)' }}>
                  {t('statusPanel.settings.tooltips.saving')}
                </Text>
              </>
            )}
            {!isSaving && lastSavedTime && !saveError && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <CheckmarkRegular style={{ color: 'var(--colorSuccessForeground1)', fontSize: '16px' }} />
                <Text size={200} style={{ color: 'var(--colorSuccessForeground1)' }}>
                  {t('statusPanel.settings.tooltips.saved')}
                </Text>
              </div>
            )}
            {saveError && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <ErrorCircleRegular style={{ color: 'var(--colorErrorForeground1)', fontSize: '16px' }} />
                <Text size={200} style={{ color: 'var(--colorErrorForeground1)' }}>
                  {t('statusPanel.settings.saveFailed')}
                </Text>
              </div>
            )}
            </div>
            {isMobile && (
              <Button appearance="secondary" size="large" onClick={handleReset}>
                {t('statusPanel.settings.resetDefaults')}
              </Button>
            )}
            {isMobile && (
              <Button appearance="primary" size="large" onClick={handleCancel}>
                {t('common.cancel')}
              </Button>
            )}
          </div>
          {!isMobile && (
            <Button appearance="secondary" onClick={handleReset}>
              {t('statusPanel.settings.resetDefaults')}
            </Button>
          )}
          {!isMobile && (
            <Button appearance="primary" onClick={handleCancel}>
              {t('common.cancel')}
            </Button>
          )}
        </DialogActions>
      </DialogSurface>
    </Dialog>
  );
};

